import { PointerEvent, Fragment, useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { css, useTheme, Theme } from '@emotion/react';
import { ArrowRight, At, Envelope, Folder, Users } from '@phosphor-icons/react';
import type { DashboardLoadResponse } from 'api/dashboard/handlers/dashboardLoadHandler';
import { AccountUserRole } from 'common/CommonConstants';
import { getPlaceholderUrl } from 'common/commonUtil';
import Api from 'client/lib/Api';
import { useUser } from 'client/lib/User';
import { DashboardOnboarding } from 'client/pages/dashboard/components/DashboardOnboarding';
import { DeploymentItem } from 'client/pages/project/components/DeploymentItem';
import { Dashboard, Sidebar, Content } from 'client/components/Layout/Dashboard';
import { Menu, MenuList } from 'client/components/Menu';
import type { MenuItemProps } from 'client/components/Menu';
import { adminSubtitle, memberSubtitle } from 'client/components/Other/AccountSelect';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { ListItemText } from 'client/components/Other/ListItemText';
import { ListSubheader } from 'client/components/Other/ListSubheader';
import { Post } from 'client/components/Post';
import { Typography } from 'client/components/Typography';
import vscodeLogo from 'client/static/image/vscode.svg';
import { mediaQuery } from 'client/Theme';
import { DashboardSubheader } from './DashboardSubheader';

const loadPins = () =>
  new Promise<DashboardLoadResponse['pins']>((resolve, reject) =>
    Api.post<object, DashboardLoadResponse>('/dashboard/load', {})
      .then(({ status, pins }) => {
        if (status !== 'ok') return reject(status);
        return resolve(pins);
      })
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

export const DashboardPage = () => {
  const { user, isLoggedIn } = useUser();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const isSingleAccount = user?.accounts.length === 1;
  const isSingleProject = (user?.projects || []).length <= 1;
  const isProjectsEmpty = (user?.projects || []).length <= 0;
  const isDeploymentsEmpty = isProjectsEmpty || (user?.projects || []).some((project) => project.deploymentIDs?.length <= 0);
  const isOnboardingShown = isSingleAccount && isSingleProject && isDeploymentsEmpty;

  const handleMenuOpen = useCallback((event: PointerEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget), []);

  const { data: pins = [] } = useQuery({
    queryKey: ['dashboard', user?.userID],
    queryFn: loadPins,
    enabled: isLoggedIn,
    refetchInterval: 30 * 1000, // Refetch every 30 secs
  });

  const pinnedItems = pins.reduce((list, pin) => {
    const project = list.find((item) => item.id === pin.project.id);
    if (project) project.items.push(pin);
    else list.push({ ...pin.project, items: [pin] });
    return list;
  }, [] as ((typeof pins)[number]['project'] & { items: typeof pins })[]);

  const accountItems = useMemo<MenuItemProps[]>(
    () =>
      (user?.accounts || []).map((account) => ({
        id: `${account.id}`,
        title: account.name,
        subtitle: account.accountUserRoleID === AccountUserRole.ADMINISTRATOR ? adminSubtitle(theme) : memberSubtitle,
        image: { alt: 'account profile picture', src: getPlaceholderUrl('account', account.id), shape: 'square' },
        href: `/${account.identifier}`,
        iconRight: <ArrowRight />,
      })),
    [user?.accounts]
  );

  const sidebarItems = useMemo(
    () => [
      <ListSubheader id="accounts">
        <Users /> Accounts
      </ListSubheader>,
      ...accountItems,
    ],
    [accountItems]
  );

  return (
    <>
      <DashboardSubheader />
      <Dashboard>
        {!isOnboardingShown && (
          <Sidebar css={sidebarStyle}>
            <MenuList items={sidebarItems} css={menuStyle} />
          </Sidebar>
        )}
        <Content css={[contentStyle, isOnboardingShown && onboardingStyle]}>
          {pinnedItems.map((project) => (
            <Fragment key={project.id}>
              <Link to={`/${project.accountIdentifier}/project/${project.id}`} css={headerStyle}>
                <ListItemFigure
                  image={{ alt: 'project image', src: getPlaceholderUrl('project', project.id), shape: 'hexagon' as const }}
                  size={40}
                />
                <ListItemText
                  primary={project.name}
                  secondary={
                    <>
                      <Folder /> Project
                    </>
                  }
                  primaryProps={{ variant: 'h5', noWrap: true }}
                  secondaryProps={{ component: 'span' }}
                  css={titleStyle}
                />
                <hr />
              </Link>

              <ol>
                {project.items.map((deployment, index) => (
                  <DeploymentItem
                    key={deployment.id}
                    index={index}
                    deployment={deployment}
                    link={`/${project.accountIdentifier}/project/${project.id}/deployment/${deployment.id}`}
                    pin={true}
                    onMenu={handleMenuOpen}
                  />
                ))}
              </ol>
            </Fragment>
          ))}
          {!pinnedItems.length && (
            <Post author={{ name: 'Staff' }} watermark={<At />}>
              <Typography variant="h4">Welcome to Diploi</Typography>
              <Typography>We're thrilled to have you here and can't wait to show you what we've got in store.</Typography>
              <Typography>
                As fellow developers, we've been using Diploi for all our projects (that we've had time to migrate at least) and it has made
                our lives so much easier.
              </Typography>
              <Typography>So what are you waiting for? Let's start Diploing and take your projects to the next level!</Typography>
              <Typography css={emailStyle}>
                <Envelope /> <a href="mailto:hello@diploi.com">hello@diploi.com</a>
              </Typography>
            </Post>
          )}
          {isOnboardingShown && <DashboardOnboarding />}
        </Content>

        <Menu
          items={[
            {
              id: 'vscode',
              title: 'Open in VS Code',
              image: { alt: 'vs code icon', src: vscodeLogo },
              href: anchorEl?.getAttribute('data-url') || '#',
              absolute: true,
            },
          ]}
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={() => setAnchorEl(null)}
          css={menuStyle}
        />
      </Dashboard>
    </>
  );
};

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;

  > ol {
    margin: 0;
    padding: 0;
    list-style: 0;
  }
`;

const onboardingStyle = css`
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  gap: 32px;
  padding: 32px;

  ${mediaQuery.large(css`
    grid-template-columns: 1fr 1fr;
    padding: 0;
    width: 100% !important;
  `)}
`;

const menuStyle = css`
  padding: 0;

  > li:first-of-type .MuiListItemSecondaryAction-root {
    display: none;
  }
`;

const sidebarStyle = (theme: Theme) => css`
  align-self: flex-start;
  margin-left: 0 !important;
  margin-right: 64px;
  padding-right: 64px;
  min-height: 400px;
  border-right: 2px solid ${theme.color.grey[60]};

  box-sizing: border-box;
`;

const emailStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;

  > svg {
    transform: translateY(1px);
  }

  > a {
    color: inherit;
  }
`;

const headerStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 16px;
  text-decoration: none;

  > hr {
    flex: 1;
    margin-left: 16px;
    height: 2px;
    background-color: ${theme.color.grey[60]};
    border: none;
  }
`;

const titleStyle = css`
  flex: unset;

  > span {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: -4px;
  }
`;
